import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { loadPrograms, type Program } from '@/services/content'
import Icon from '@/components/ui/Icon'
import styles from './Programs.module.css'

export default function Programs() {
  const navigate = useNavigate()
  const [programs, setPrograms] = useState<Program[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadPrograms()
      .then(setPrograms)
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading programs...</div>
      </div>
    )
  }

  const freePrograms = programs.filter(p => !p.isPremium)
  const premiumPrograms = programs.filter(p => p.isPremium)

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Guided Programs</h1>
        <p className={styles.subtitle}>
          Follow a step-by-step plan. See change in 1-3 weeks.
        </p>
      </header>

      {freePrograms.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Free to start</h2>
          <div className={styles.programList}>
            {freePrograms.map((program) => (
              <ProgramCard
                key={program.id}
                program={program}
                onClick={() => navigate(`/programs/${program.id}`)}
              />
            ))}
          </div>
        </section>
      )}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>All programs</h2>
        <div className={styles.programList}>
          {premiumPrograms.map((program) => (
            <ProgramCard
              key={program.id}
              program={program}
              onClick={() => navigate(`/programs/${program.id}`)}
            />
          ))}
        </div>
      </section>
    </div>
  )
}

function ProgramCard({ program, onClick }: { program: Program; onClick: () => void }) {
  return (
    <button className={styles.programCard} onClick={onClick}>
      {program.coverImage ? (
        <div className={styles.programCoverImage}>
          <img src={program.coverImage} alt={program.title} className={styles.programCoverImg} />
          <span className={styles.programDurationOverlay}>{program.duration} days</span>
        </div>
      ) : (
        <div
          className={styles.programCover}
          style={{ backgroundColor: program.coverColor }}
        >
          <span className={styles.programIcon}><Icon name={program.icon} size={32} /></span>
          <span className={styles.programDuration}>{program.duration} days</span>
        </div>
      )}
      <div className={styles.programInfo}>
        <div className={styles.programHeader}>
          <h3 className={styles.programTitle}>{program.title}</h3>
          {program.isPremium && (
            <span className={styles.premiumBadge}>Premium</span>
          )}
          {!program.isPremium && (
            <span className={styles.freeBadge}>Free</span>
          )}
        </div>
        <p className={styles.programSubtitle}>{program.subtitle}</p>
        <div className={styles.programMeta}>
          <span>{program.duration} days</span>
          <span>·</span>
          <span>{program.days.filter(d => d.activities.length > 0).length} activities</span>
        </div>
      </div>
    </button>
  )
}
